import { Injectable, inject } from '@angular/core';
import { IResponseMeta } from '@df/api/request/request.service';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GTMServiceEvent, GtmService } from '@df/module-gtm/gtm.service';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { BasketService } from '@df/session/api/basket/basket.service';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import compact from 'lodash-es/compact';
import { EListingLabelOptionId, LocalProduct } from '../../catalog/model/product/product.decorator';
import { Events } from '@df/core/service/event';
declare module '@df/module-gtm/gtm.namespace' {
    namespace Gtm {
        export interface IProductImpressionData {
            in_stock?: boolean;
            metric1?: number;
            currency?: string;
            simple_name?: string;
            numberOfResults?: number;
        }
        export interface IEcommerceItem {
            item_stock?: string;
        }
        export interface ITrackImpressionData {
            numberOfResults?: number;
        }
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            gtm_product_metrics?: { [key: string]: string };
        }
    }
}
export enum EGtmAddToCartVia {
    pdp = 'PDP',
    quick = 'Quick Buy',
    outfit = 'Outfit Builder',
    bundle = 'Bundle',
    trade = 'Trade Bulk Buy',
    carouselQuick = 'Carousel Quick Buy'
}
@Injectable({
    providedIn: 'root'
})
export class LocalGtmService extends GtmService {
    readonly addToCartGtmBeEvent = 'addToCart';

    private basketService = inject(BasketService);
    private eventService = inject(Events);

    protected override setListeners(): void {
        super.setListeners();

        // after we create object, setListeners is called in costructor, se we need to delay
        this.timeoutService.setTimeout(() => {
            // track qty changes https://tomandco.atlassian.net/browse/LER-1839
            this.subscriptions.add(
                this.basketService.changeQtySuccessSubject.subscribe(event => {
                    const qty = event.request.qty;
                    const eventData: Gtm.IEvent = {
                        event: event['previousQty'] && event['previousQty'] > qty ? 'product_quantity_down' : 'product_quantity_up'
                    };
                    this.events.next(new GTMServiceEvent(eventData));
                    this.push(eventData);
                })
            );
        });

        this.setNoShippingMethodsTracking();
    }
    override toProductData(model?: Product | BasketItem): Gtm.IProductImpressionData | undefined {
        if (!model) {
            return undefined;
        }
        const productData: Gtm.IProductImpressionData | undefined = super.toProductData(model);
        if (!productData) {
            return undefined;
        }
        productData.in_stock = model.isInStock();
        productData.currency = this.app.data.currency.base;
        productData.simple_name = (<IProductDataV2>model.data).name;
        if (productData.quantity) {
            productData.metric1 = (productData.price * 100 * productData.quantity) / 100;
        }
        return productData;
    }
    override getCheckoutStepOptions(stepName: string, checkout: CheckoutFormComponentDef): string {
        if (stepName === 'payment') {
            const paymentMethod = checkout.paymentMethod; // Changing payment method code to method title
            return paymentMethod?.title || 'not selected';
        }
        return super.getCheckoutStepOptions(stepName, checkout);
    }
    override onPaymentMethodChanged(data: Checkout.ISavePaymentMethodEventData): void {
        const stepIndex = this.getStepIndexByName('payment');
        if (stepIndex > 0) {
            this.trackCheckoutOption(stepIndex, data.methodDetails?.title || 'not selected');
        }
    }
    gtmAddToCartBeEvent(result: any, addedVia: EGtmAddToCartVia): void {
        if (result?.gtm) {
            (Array.isArray(result.gtm) ? result.gtm : [result.gtm]).forEach((data: Gtm.IEventEcommerce) => {
                if (data.event === this.addToCartGtmBeEvent) {
                    data.ecommerce.added_via = addedVia;
                    this.trackBackendEvent(data, undefined, true);
                }
            });
        }
    }
    protected override trackBackendEvent(eventData: Gtm.IEvent, response?: IResponseMeta, add = false) {
        if (eventData.event === this.eventsMap.checkoutSuccess && eventData['event-data'] && response?.result?.success?.payment_method) {
            eventData['event-data'] = { ...eventData['event-data'], paymentMethod: response?.result?.success?.payment_method };
            eventData['event-data'].customer = {
                ...eventData['event-data'].customer,
                ...response?.result?.success?.billing
            };
        }
        if (eventData.event !== this.addToCartGtmBeEvent || add) {
            super.trackBackendEvent(eventData, response);
        }
    }
    public override getEcommerceItems(model: Product | Product[]): Gtm.IEcommerceItem[] {
        if (Array.isArray(model)) {
            return <Gtm.IEcommerceItem[]>compact(
                model.map((model: Product, index: number) => ({
                    ...this.toEcommerceItemData(model, index),
                    item_stock: this.getItemStock(model as LocalProduct)
                }))
            );
        }
        const data = this.toEcommerceItemData(model);
        if (data) {
            return compact([
                {
                    ...data,
                    item_stock: this.getItemStock(model as LocalProduct)
                }
            ]);
        }
        return [];
    }
    private getItemStock(model: LocalProduct): string {
        return (optionLabelPipe(this.getItemStockLabel(model), 'listing_label') as string) || 'In Stock';
    }
    private getItemStockLabel(model: LocalProduct): number | undefined {
        if (model.hasV2) {
            if (model.isConfigurable || model.isGrouped) {
                const plpLabel = (model.data as IProductDataV2).plp_label;
                if (plpLabel === 'Coming Soon (Forced)' || plpLabel === 'Coming Soon') {
                    return EListingLabelOptionId.comingSoon;
                }
                if (plpLabel === 'Pre-Order') {
                    return EListingLabelOptionId.preOrder;
                }
                if (plpLabel === 'Back Soon') {
                    return EListingLabelOptionId.backSoon;
                }
                if (plpLabel === 'Back Order') {
                    return EListingLabelOptionId.backOrder;
                }
                if (plpLabel === 'Sold Out') {
                    return EListingLabelOptionId.soldOut;
                }
            }
            switch (true) {
                case model.isComingSoon || model.isComingSoonForced:
                    return EListingLabelOptionId.comingSoon;
                case model.isSoldOut:
                    return EListingLabelOptionId.soldOut;
                case model.isBackSoon:
                    return EListingLabelOptionId.backSoon;
                case model.isBackOrder:
                    return EListingLabelOptionId.backOrder;
                case model.isPreOrder:
                    return EListingLabelOptionId.preOrder;
            }
        }
        return undefined;
    }
    override getAdditionalProductData(product: Product<IProductDataV2>) {
        const superData = super.getAdditionalProductData(product);
        const metrics = this.app.data.gtm_product_metrics;

        if (metrics) {
            const data = product?.data;
            Object.keys(metrics).forEach(key => {
                if (data?.[key]) {
                    superData[key] = data[key];
                }
            });
        }
        return superData;
    }

    private setNoShippingMethodsTracking() {
        this.subscriptions.add(
            this.eventService.on(Checkout.EVENTS.StepChange).subscribe((event: Checkout.IStepChangeEventData) => {
                if (event.stepName === 'deliveryOptions') {
                    const checkoutData = event.checkout.data;
                    if (!event.checkout.backendData.shipping?.methods?.length) {
                        const e = {
                            event: 'checkoutNoShippingMethods',
                            country: checkoutData.address?.shipping?.country_id,
                            postcode: checkoutData.address?.shipping?.postcode,
                            skus: this.basket.items.map((item: BasketItem) => item.product!.data!.sku).join(',')
                        };
                        this.push(e);
                    }
                }
            })
        );
    }
}
